/**
 * @file LoginPage.tsx
 * @brief Email + password login form wired to `POST /api/auth/login`.
 *
 * @details
 * - Uses `react-hook-form` + a Zod schema (mirrors the backend's
 *   `loginSchema`) for client-side validation.
 * - Maps the backend's documented error codes
 *   (`INVALID_CREDENTIALS`, `ACCOUNT_SUSPENDED`, `ACCOUNT_DELETED`,
 *   `COMPANY_SUSPENDED`, `COMPANY_DELETED`) to friendly user messages.
 * - On success, redirects role-appropriately: super admin → `/admin`,
 *   company admin → `/company`, plain user → `/app/meetings` (Boardroom web).
 * - Visual presentation is cardless and stacked to match the Facebook-
 *   style auth split layout in `AuthLayout`.
 *
 * @dependencies
 * - react-hook-form
 * - @hookform/resolvers/zod
 * - zod
 * - shared/auth/AuthContext
 * - shared/api/client (ApiClientError)
 *
 * @references
 * - BACKEND/src/features/auth/routes.js (POST /login error codes)
 * - BACKEND/src/features/auth/service.js (loginUser response shape)
 *
 * - Two-step verification (B31, 25-10-2026): when password, Google, Apple
 *   or Microsoft answers TWO_FACTOR_REQUIRED / TWO_FACTOR_SETUP_REQUIRED the
 *   page shows the code / setup-only screen (`TwoFactorSignInStep`) instead
 *   of the form; no error line.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 27-07-2026 — cardless Facebook-style form column
 * @updated 25-10-2026 — two-step verification (B31)
 */

import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Building2, Eye, EyeOff, LoaderCircle, Lock, Mail } from "lucide-react";
import { useAuth } from "@/shared/auth/auth-context";
import {
  allowedPathPrefixForUser,
  homePathForUser,
} from "@/shared/auth/homePath";
import { ApiClientError } from "@/shared/api/client";
import { SocialSignIn } from "@/features/auth/SocialSignIn";
import { challengeFromError } from "@/features/auth/twoFactor/twoFactorApi";
import { TwoFactorSignInStep } from "@/features/auth/twoFactor/TwoFactorSteps";
import type { RegisteredState } from "@/features/auth/register/registerShared";
import { Button } from "@/components/ui/Button";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { Input } from "@/components/ui/Input";

// ----------------------------------------------------------------------
// Schema
// Mirrors BACKEND loginSchema (8-char minimum) so validation matches the
// server. We keep min(1) on email so empty submissions show the cleaner
// "Email is required" message before the format check fires.
// ----------------------------------------------------------------------
const formSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

type FormValues = z.infer<typeof formSchema>;

// ----------------------------------------------------------------------
// Map backend error codes to user-facing messages.
// Source: BACKEND/src/features/auth/routes.js POST /login
// ----------------------------------------------------------------------
function describeAuthError(err: unknown): string {
  if (err instanceof ApiClientError) {
    switch (err.code) {
      case "INVALID_CREDENTIALS":
        return "Incorrect email or password.";
      case "ACCOUNT_SUSPENDED":
        return "Your account has been suspended. Contact support.";
      case "ACCOUNT_DELETED":
        return "This account no longer exists.";
      case "COMPANY_SUSPENDED":
        return "Your company account is currently suspended.";
      case "COMPANY_DELETED":
        return "Your company account has been removed.";
      case "INVALID_GOOGLE_TOKEN":
        return "Google sign-in could not be verified.";
      case "INVALID_APPLE_TOKEN":
        return "Apple sign-in could not be verified.";
      case "GOOGLE_ACCOUNT_MISMATCH":
        return "This email is already linked to a different Google account.";
      case "APPLE_ACCOUNT_MISMATCH":
        return "This email is already linked to a different Apple ID.";
      case "TWO_FACTOR_UNAVAILABLE":
        return "Two-step verification is not available right now. Please try again later.";
      default:
        return err.message || "Login failed. Please try again.";
    }
  }
  if (err instanceof Error && err.message) return err.message;
  return "Unexpected error. Please try again.";
}

export function LoginPage() {
  const { login, twoFactorChallenge } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  // Just signed up (01-10-2026): the email is filled in and the password
  // has focus, as the phone lands on Login after "account created".
  const registered = (location.state as Partial<RegisteredState> | null)
    ?.registeredEmail;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: registered ?? "", password: "" },
  });

  // ----------------------------------------------------------------------
  // Submit handler
  // ----------------------------------------------------------------------
  // Decide where to send the user after a successful login.
  //
  //   - super admin → /admin
  //   - company admin → /company
  //   - plain user → /app/meetings (Boardroom web parity, 28-07-2026)
  //
  // If the guard pushed us here from a deep link (`location.state.from`),
  // honour it ONLY when the path is inside the user's role-space.
  // ----------------------------------------------------------------------
  const goHome = (user: Parameters<typeof homePathForUser>[0]) => {
    const home = homePathForUser(user);
    const allowedPrefix = allowedPathPrefixForUser(user);
    const from = (location.state as { from?: { pathname?: string; search?: string } } | null)
      ?.from;
    const fromState = from?.pathname;
    // An AI app's "connect to Verblizr" consent (MCP OAuth, 06-10-2026) is
    // outside every role-space and needs its query (?request=…) back.
    if (fromState === "/oauth/consent") {
      navigate(`${fromState}${from?.search ?? ""}`, { replace: true });
      return;
    }
    const fromIsCompatible =
      Boolean(fromState) &&
      fromState !== "/login" &&
      fromState!.startsWith(allowedPrefix);
    navigate(fromIsCompatible ? fromState! : home, { replace: true });
  };

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      goHome(await login(values));
    } catch (err) {
      // The second step shows its own screen (B31).
      if (!challengeFromError(err)) setServerError(describeAuthError(err));
    }
  };

  if (twoFactorChallenge) {
    return <TwoFactorSignInStep onSignedIn={goHome} onCancel={() => setServerError(null)} />;
  }

  return (
    <div className="flex flex-col items-stretch">
      <div className="mb-8 text-center">
        <h2 className="text-2xl font-bold tracking-tight text-foreground">
          Sign in
        </h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Use your Verblizr account credentials.
        </p>
      </div>

      <SocialSignIn
        disabled={isSubmitting}
        onSignedIn={(user) => {
          setServerError(null);
          goHome(user);
        }}
        onError={(err) => {
          if (!challengeFromError(err)) setServerError(describeAuthError(err));
        }}
      />

      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" />
        or
        <span className="h-px flex-1 bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3.5" noValidate>
        {/* ------------------------------------------------------------------
            Email field
            ------------------------------------------------------------------ */}
        {/* Bare fields with glyphs, as the phone's Login sheet (DES 01-10-2026:
            "login screen bhi"). */}
        <FormFieldGroup
          label="Email"
          htmlFor="email"
          icon={<Mail />}
          error={errors.email?.message}
        >
          <Input
            id="email"
            type="email"
            autoComplete="email"
            autoFocus={!registered}
            aria-invalid={errors.email ? "true" : "false"}
            {...register("email")}
          />
        </FormFieldGroup>

        {/* ------------------------------------------------------------------
            Password field with show/hide toggle
            ------------------------------------------------------------------ */}
        <FormFieldGroup
          label="Password"
          htmlFor="password"
          icon={<Lock />}
          error={errors.password?.message}
        >
          <div className="flex items-center gap-2">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              autoFocus={Boolean(registered)}
              className="flex-1"
              aria-invalid={errors.password ? "true" : "false"}
              {...register("password")}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          </div>
        </FormFieldGroup>

        {/* ------------------------------------------------------------------
            Server error banner
            ------------------------------------------------------------------ */}
        {serverError ? (
          <div
            role="alert"
            className="rounded-2xl border border-destructive/30 bg-destructive/5 px-3 py-2.5 text-xs text-destructive"
          >
            {serverError}
          </div>
        ) : null}

        <Button
          type="submit"
          className="mt-1 h-12 w-full text-[15px] font-semibold"
          disabled={isSubmitting}
          size="lg"
        >
          {isSubmitting ? (
            <>
              <LoaderCircle className="h-4 w-4 animate-spin" />
              Signing in…
            </>
          ) : (
            "Continue"
          )}
        </Button>

        <Button
          type="button"
          variant="ghost"
          // White pill, no border, like the sign-in buttons (DES 01-10-2026).
          className="h-12 w-full rounded-full bg-app-card text-[15px] font-semibold hover:bg-app-quaternary"
          asChild
        >
          <Link to="/forgot-password">Forgot password?</Link>
        </Button>
      </form>

      {/* Sign-up, as the phone's Login: create an account, then a business
          link under a divider (01-10-2026). */}
      <div className="mt-6 flex flex-col items-center gap-4 border-t border-border pt-6">
        <Button
          type="button"
          variant="secondary"
          // The quaternary grey: the secondary fill all but vanished on the
          // page colour once the login matched the app (DES 01-10-2026).
          className="h-12 w-full bg-app-quaternary text-[15px] font-semibold hover:bg-app-quaternary-dark"
          asChild
        >
          <Link to="/register">Create an account</Link>
        </Button>
        <Link
          to="/register/business"
          className="flex items-center gap-2 text-sm font-medium text-foreground hover:underline"
        >
          <Building2 className="h-4 w-4" />
          Register as a business
        </Link>
      </div>
    </div>
  );
}
